import { useRef, useState, type ReactNode, type Ref } from "react";
import { DialView } from "../../components/DialView";
import { Icon } from "../../components/Icon";
import type { DialVM } from "../../dialModel";
import type { CompareFollows } from "./useCompareMode";
import { fmtCompareDate, type CompareDirection } from "./compareModel";

/**
 * Look back's two dials: Then on the left, Now on the right beside the card it projects onto.
 *
 * They are ONE component at one size given ONE rotation, so they can't drift apart: the angle is computed
 * once, upstream, from the single selection both render. Picking on either calls the same selection
 * handlers, so a pick on Then moves Now, the breadcrumb and the card exactly as a pick on Now does.
 *
 * Below 920px the two are drawn in the same place and a Then | Now toggle (or a sideways swipe) fades
 * between them — geometry, selection and rotation don't move, only which face is showing.
 */
export function DialPair({
  thenVm, nowVm, day, loading, follows, onFollows, selectedSectionKey, selectedSegmentKey,
  rotationDeg, rotationMs, showTie, onSelectSection, onSelectSegment, nowDialRef, onEditCentreLogo,
  centreLogoNote, legend, caption, direction = "back",
}: {
  /** KEY-890: back (Then, on the left) or ahead (how it will be, on the right — the future is that way). */
  direction?: CompareDirection;
  /** Null until the first snapshot for this dial lands: the Then slot shows a placeholder disc. */
  thenVm: DialVM | null;
  nowVm: DialVM;
  day: string;
  loading: boolean;
  follows: CompareFollows;
  onFollows: (f: CompareFollows) => void;
  selectedSectionKey: string | null;
  selectedSegmentKey: string | null;
  rotationDeg: number;
  rotationMs: number;
  /** The dotted tie across the gap — Dynamic, wide layout, something selected, not mid-turn. */
  showTie: boolean;
  onSelectSection: (sectionKey: string) => void;
  onSelectSegment: (sectionKey: string, segmentKey: string | null) => void;
  /** The Now dial is what the beam leaves from. */
  nowDialRef: Ref<HTMLDivElement>;
  onEditCentreLogo?: () => void;
  centreLogoNote?: string;
  legend: ReactNode;
  caption?: string;
}) {
  const date = fmtCompareDate(day);
  const ahead = direction === "ahead";
  const word = ahead ? "Ahead" : "Then";
  const [shown, setShown] = useState<CompareFollows>("now");
  const swipeFrom = useRef<number | null>(null);

  const shared = {
    progress: true,
    selectedSectionKey,
    selectedSegmentKey,
    rotationDeg,
    rotationMs,
    onSelectSection,
    onSelectSegment,
  };

  return (
    <div className="kd-dialpair" data-shown={shown} data-direction={direction}>
      <div className="kd-dialpair-chips" role="radiogroup" aria-label="Which dial the card follows">
        <button
          type="button"
          role="radio"
          aria-checked={follows === "then"}
          className="kd-dialpair-chip kd-focusable"
          data-kind="then"
          onClick={() => onFollows("then")}
        >
          {loading ? <span className="kd-rangebar-spinner" aria-hidden="true" /> : <Icon name={ahead ? "calendar" : "history"} size={14} />}
          {word} · {date}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={follows === "now"}
          className="kd-dialpair-chip kd-focusable"
          data-kind="now"
          onClick={() => onFollows("now")}
        >
          <span className="kd-live-dot" aria-hidden="true" />
          Now · live
        </button>
      </div>

      <div className="kd-dialpair-toggle" role="radiogroup" aria-label="Show Now or Then">
        {(["then", "now"] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={shown === k}
            className="kd-dialpair-seg kd-focusable"
            onClick={() => setShown(k)}
          >
            {k === "then" ? word : "Now"}
          </button>
        ))}
      </div>

      <div
        className="kd-dialpair-row"
        onPointerDown={(e) => { swipeFrom.current = e.clientX; }}
        onPointerUp={(e) => {
          const from = swipeFrom.current;
          swipeFrom.current = null;
          if (from === null || Math.abs(e.clientX - from) < 48) return;
          // Only the stacked (phone) layout has a hidden face to swipe to; on a wide screen both are showing.
          setShown(e.clientX < from ? "now" : "then");
        }}
      >
        {/* KEY-890: it slides out from Now when the mode opens, and turns — clockwise ahead, anticlockwise back —
            showing today's face, faded, until the other day's arrives. */}
        <figure
          className="kd-dialpair-dial"
          data-kind="then"
          data-loading={loading || !thenVm ? "true" : undefined}
          aria-label={ahead ? `Ahead dial, as it will be on ${date} if nothing is done` : `Then dial, as it was on ${date}`}
          aria-busy={loading || !thenVm}
        >
          <div className="kd-dialpair-turn">
            <DialView {...shared} vm={thenVm ?? nowVm} variant="then"
              ariaLabel={ahead ? `Ahead dial, as it will be on ${date} if nothing is done` : `Then dial, as it was on ${date}`} />
          </div>
        </figure>
        {showTie && (
          <span className="kd-dialpair-tie" aria-hidden="true">
            <span className="kd-dialpair-tie-badge"><Icon name="chevRight" size={12} /></span>
          </span>
        )}
        <figure className="kd-dialpair-dial" data-kind="now" aria-label="Now dial, live">
          <div ref={nowDialRef} style={{ width: "100%", height: "100%" }}>
            <DialView {...shared} vm={nowVm} ariaLabel="Now dial, live" onEditCentreLogo={onEditCentreLogo} centreLogoNote={centreLogoNote} />
          </div>
        </figure>
      </div>

      {legend}
      <p className="kd-dialpair-caption">
        {caption ?? (ahead
          ? `If nothing is done: what's already scheduled — due dates, changes back and resets — up to ${date}. Selecting on either dial selects the same node on both.`
          : "Selecting on either dial selects the same node on both. Then is drawn on today's structure.")}
      </p>
    </div>
  );
}
